import * as _ from 'underscore';
import { DataConnectionErrorCode } from '../helpers/ErrorCodes';
import { Filter } from '../helpers/Filter';
import { ResultHelper } from '../helpers/ResultHelper';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { Module } from '../Module';
import { Flows } from '../resources/Flows';
import { OAuthCredentials } from '../resources/OAuthCredentials';
import { Workbooks } from '../resources/Workbooks';
import { GeneratePublicKeyRequest } from '../ServerApi/requests/GeneratePublicKeyRequest';
import { RevertFlowResult } from '../ServerApi/requests/RevertFlowRequest';
import { IRevertWorkbookResult } from '../ServerApi/requests/RevertWorkbookRequest';
import {
  ConnectionAuthInfo,
  IDatabaseConnectionAuthInfo,
  IOAuthConnectionAuthInfo,
} from '../ServerApi/types/IConnectionAuthInfo';
import {
  EncryptedConnectionKeychain,
  IOAuthKeychain,
  UnencryptedConnectionKeychain,
} from '../ServerApi/types/IConnectionKeyChain';
import { FlowRevision, IWorkbookRevision } from '../ServerApi/types/IContentRevision';
import { IOAuthCredential } from '../ServerApi/types/IOAuthCredential';
import { AddOAuthCredentialAction } from './AddOAuthCredentialAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ErrorNotifyingServer } from './ErrorNotifyingServer';
import { IModalScope, ModalService, ModalSize } from './ModalService';

const AddOAuthCredentialsOptionValue = 'new';

export interface IDatabaseCredentialsDialogScope extends ng.IScope {
  connection: IDatabaseConnectionAuthInfo;
  dialog: {
    dialogDescription: string;
  };
}

export interface IOAuthCredentialsDialogScope extends IModalScope {
  connection: IOAuthConnectionAuthInfo;
  dialog: {
    signIn: (connection: IOAuthConnectionAuthInfo) => ng.IPromise<any>;
    addOAuthCredentialsOptionText: string;
    addOAuthCredentialsOptionValue: string;
    advisoryText: string;
    busy: boolean;
    chooseCredentialsText: string;
    oAuthCredentials: IOAuthCredential[];
    selectedOAuthCredentialsId: string;
    title: string;
    dialogDescription: string;
  };
}

export class CheckConnectionCredentialsAction {

    public static $inject = [
        '$q',
        '$rootScope',
        '$translate',
        'AddOAuthCredentialAction',
        'ConfirmActionDialog',
        'ErrorNotifyingServer',
        'Flows',
        'ModalService',
        'Workbooks'];

    constructor(
        private $q: ng.IQService,
        private $rootScope: ng.IRootScopeService,
        private $translate: ng.translate.ITranslateService,
        private AddOAuthCredentialAction: AddOAuthCredentialAction,
        private ConfirmActionDialog: ConfirmActionDialog,
        private ErrorNotifyingServer: ErrorNotifyingServer,
        private Flows: Flows,
        private ModalService: ModalService,
        private Workbooks: Workbooks) { }

    public showCheckCredentialsDialogs(
      connection: ConnectionAuthInfo,
      revision: IWorkbookRevision | FlowRevision,
      authenticatedKeychains: UnencryptedConnectionKeychain[] = []
    ): ng.IPromise<IRevertWorkbookResult> {
      let dialogsDeferred;
      if (this.isWorkbookRevision(revision)) {
        dialogsDeferred = this.$q.defer<IRevertWorkbookResult>();
      }
      if (this.isFlowRevision(revision)) {
        dialogsDeferred = this.$q.defer<RevertFlowResult>();
      }

      this.showCheckCredentialsDialog(connection, revision, authenticatedKeychains, dialogsDeferred);

      return dialogsDeferred.promise;
    }

    private showCheckCredentialsDialog(
      connection: ConnectionAuthInfo,
      revision: IWorkbookRevision | FlowRevision,
      authenticatedKeychains: UnencryptedConnectionKeychain[] = [],
      dialogsDeferred: ng.IDeferred<IRevertWorkbookResult | RevertFlowResult>
    ) {
      const dialogPromise = connection.isOAuth
        ? this.showOAuthConnectionDialog(
            <IOAuthConnectionAuthInfo>connection,
            revision,
            authenticatedKeychains,
            dialogsDeferred
          )
        : this.showDatabaseConnectionDialog(
            <IDatabaseConnectionAuthInfo>connection,
            revision,
            authenticatedKeychains,
            dialogsDeferred
          );
      dialogPromise.then(revertResult => {
        // Pass any revert workbook error out to the revert workbook action handler
        if (ResultHelper.hasErrors(revertResult)) {
          dialogsDeferred.resolve(revertResult);
        }
      });
    }

    private showDatabaseConnectionDialog(
      connection: IDatabaseConnectionAuthInfo,
      revision: IWorkbookRevision | FlowRevision,
      authenticatedKeychains: UnencryptedConnectionKeychain[] = [],
      dialogsDeferred: ng.IDeferred<IRevertWorkbookResult | RevertFlowResult>
    ): ng.IPromise<IRevertWorkbookResult | RevertFlowResult> {
      const scope = <IDatabaseCredentialsDialogScope>this.$rootScope.$new();
      scope.connection = connection;
      scope.connection.keychain.embedCredentials = true; // default embed credentials to true
      // if not a Workbook, it is a Flow
      scope.dialog = {
        dialogDescription: this.isWorkbookRevision(revision) ?
          this.$translate.instant('revisionHistoryCredentialsDialogDatabaseDescriptionForWorkbook')
          : this.$translate.instant('revisionHistoryCredentialsDialogDatabaseDescriptionForFlow')
      };

      const dialogInstance = this.ConfirmActionDialog.open({
        title: this.$translate.instant('revisionHistoryDatabaseCredentialsDialogTitle'),
        bodyTemplateUrl: 'checkDatabaseConnectionCredentialsDialog.html',
        scope: scope,
        size: ModalSize.fitToContent,
        actionName: this.$translate.instant('signIn'),
        action: (dialogInstance: IConfirmActionDialogInstance) => {
          if (this.isWorkbookRevision(revision)) {
            return this.revertWorkbook(scope.connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
            // check that the credentials are valid for the connection
            .then(revertWorkbookResult => {
              if (ResultHelper.hasErrors(revertWorkbookResult) && revertWorkbookResult.errors[0].code === DataConnectionErrorCode.FailedConnectionCredentials) {
                dialogInstance.showErrorMessage(this.$translate.instant('signInFailedBadCredentials'));
              }
              // pass along the results
              return revertWorkbookResult;
            });
          } else if (this.isFlowRevision(revision)) {
            return this.revertFlow(scope.connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
            .then(revertFlowResult => {
              if (ResultHelper.hasErrors(revertFlowResult) && revertFlowResult.errors[0].code === DataConnectionErrorCode.FailedConnectionCredentials) {
                dialogInstance.showErrorMessage(this.$translate.instant('signInFailedBadCredentials'));
              }
              return revertFlowResult;
            });
          }
        }
      });

      dialogInstance.result.catch(() => dialogsDeferred.reject());

      return dialogInstance.result;
    }

    private showOAuthConnectionDialog(
      connection: IOAuthConnectionAuthInfo,
      revision: IWorkbookRevision | FlowRevision,
      authenticatedKeychains: UnencryptedConnectionKeychain[] = [],
      dialogsDeferred: ng.IDeferred<IRevertWorkbookResult>
    ): ng.IPromise<IRevertWorkbookResult> {
      const scope = <IOAuthCredentialsDialogScope>this.$rootScope.$new();

      scope.dialog = {
        signIn: null,
        addOAuthCredentialsOptionText: this.$translate.instant('addOAuthCredential', { CONNECTION_TYPE_NAME: connection.connectionTypeDisplayName }),
        addOAuthCredentialsOptionValue: AddOAuthCredentialsOptionValue,
        advisoryText: this.getOAuthAdvisoryText(connection),
        busy: false,
        chooseCredentialsText: this.$translate.instant('revisionHistoryOAuthChooseCredentialsLabel', {
          TYPE_NAME: connection.connectionTypeDisplayName
        }),
        oAuthCredentials: [],
        selectedOAuthCredentialsId: '',
        title: this.$translate.instant('revisionHistoryOAuthCredentialsDialogTitle', {
          TYPE_NAME: connection.connectionTypeDisplayName
        }),
        // if not a Workbook, it is a Flow
        dialogDescription: this.isWorkbookRevision(revision) ?
          this.$translate.instant('revisionHistoryCredentialsDialogOAuthDescriptionForWorkbook')
          : this.$translate.instant('revisionHistoryCredentialsDialogOAuthDescriptionForFlow')
      };

      const modalInstance = this.ModalService.open({
        templateUrl: 'checkOAuthConnectionCredentialsDialog.html',
        scope: scope,
        resolve: {
          oAuthCredentials: ['OAuthCredentials', (OAuthCredentials: OAuthCredentials) => {
            return this.getOAuthCredentials(connection, OAuthCredentials);
          }]
        },
        controller: [
          '$scope', 'OAuthCredentials', 'oAuthCredentials', (dialogScope: IOAuthCredentialsDialogScope, OAuthCredentials: OAuthCredentials, oAuthCredentials: IOAuthCredential[]) => {
            dialogScope.dialog.oAuthCredentials = oAuthCredentials;

            dialogScope.dialog.signIn = () => {
              dialogScope.dialog.busy = true;
              // prevent closing of the dialog while we are waiting for the promise to resolve
              dialogScope.options.keyboard = false;
              dialogScope.options.backdrop = 'static'; // static backdrops don't close the modal on click.

              const oAuthIdPromise = dialogScope.dialog.selectedOAuthCredentialsId
                ? this.$q.when(dialogScope.dialog.selectedOAuthCredentialsId)
                : this.addOAuthCredential(connection);
              if (this.isWorkbookRevision(revision)) {
                return oAuthIdPromise.then(oAuthId => {
                  connection.keychain.oAuthKeyId = oAuthId;
                  this.revertWorkbook(connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
                    .then(revertWorkbookResult => {
                      return dialogScope.close(revertWorkbookResult);
                    });
                });
              } else if (this.isFlowRevision(revision)) {
                return oAuthIdPromise.then(oAuthId => {
                  connection.keychain.oAuthKeyId = oAuthId;
                  this.revertFlow(connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
                    .then(revertFlowResult => {
                      return dialogScope.close(revertFlowResult);
                    });
                });
              }

            };

            dialogScope.$watch(() => dialogScope.dialog.selectedOAuthCredentialsId, (credentialId: string) => {
              if (credentialId === AddOAuthCredentialsOptionValue) {
                dialogScope.dialog.selectedOAuthCredentialsId = undefined;

                this.addOAuthCredential(connection).then(credentialId => {
                  this.getOAuthCredentials(connection, OAuthCredentials).then(oAuthCredentials => {
                    dialogScope.dialog.oAuthCredentials = oAuthCredentials;
                    // Set the credentialId if it exists in the list
                    if(_.find(dialogScope.dialog.oAuthCredentials, (credential: IOAuthCredential) => credential.id === credentialId)) {
                      dialogScope.dialog.selectedOAuthCredentialsId = credentialId;
                    }
                  });
                });
              }
            });
          }]
      });

      modalInstance.result.catch(() => dialogsDeferred.reject());
      modalInstance.result.finally(() => scope.$destroy());

      return modalInstance.result;
    }

    private revertWorkbook(
      newKeychain: UnencryptedConnectionKeychain,
      revision: IWorkbookRevision,
      authenticatedKeychains: UnencryptedConnectionKeychain[],
      dialogsDeferred: ng.IDeferred<IRevertWorkbookResult>
    ): ng.IPromise<IRevertWorkbookResult> {
      return this.ErrorNotifyingServer.sendRequest(new GeneratePublicKeyRequest())
        .then(generatedPublicKey => {
          const unencryptedKeychains = _.clone(authenticatedKeychains);
          unencryptedKeychains.push(newKeychain);
          const encryptedKeychains = this.encryptKeychains(unencryptedKeychains, generatedPublicKey.key);

          return this.Workbooks.revertWorkbook(
            revision.workbookId,
            revision.versionNumber,
            encryptedKeychains,
            generatedPublicKey.keyId
          );
        })
        // check that the credentials are valid for the connection
        .then(revertWorkbookResult => {
          if (revertWorkbookResult && revertWorkbookResult.failedConnection) {
            // push on authenticated unencrypted keychain
            authenticatedKeychains.push(newKeychain);
            this.showCheckCredentialsDialog(revertWorkbookResult.failedConnection, revision, authenticatedKeychains, dialogsDeferred);
          } else if (!ResultHelper.hasErrors(revertWorkbookResult)){
            dialogsDeferred.resolve(revertWorkbookResult);
          }
          // pass along the results
          return revertWorkbookResult;
        });
    }

    private revertFlow(
      newKeychain: UnencryptedConnectionKeychain,
      revision: FlowRevision,
      authenticatedKeychains: UnencryptedConnectionKeychain[],
      dialogsDeferred: ng.IDeferred<RevertFlowResult>
    ): ng.IPromise<RevertFlowResult> {
      return this.ErrorNotifyingServer.sendRequest(new GeneratePublicKeyRequest())
        .then(generatedPublicKey => {
          const unencryptedKeychains = _.clone(authenticatedKeychains);
          unencryptedKeychains.push(newKeychain);
          const encryptedKeychains = this.encryptKeychains(unencryptedKeychains, generatedPublicKey.key);

          return this.Flows.revertFlow(
            revision.flowId,
            revision.versionNumber,
            encryptedKeychains,
            generatedPublicKey.keyId
          );
        })
        // check that the credentials are valid for the connection
        .then(revertFlowResult => {
          if (revertFlowResult && revertFlowResult.failedConnection) {
            // push on authenticated unencrypted keychain
            authenticatedKeychains.push(newKeychain);
            this.showCheckCredentialsDialog(revertFlowResult.failedConnection, revision, authenticatedKeychains, dialogsDeferred);
          } else if (!ResultHelper.hasErrors(revertFlowResult)){
            dialogsDeferred.resolve(revertFlowResult);
          }
          // pass along the results
          return revertFlowResult;
        });
    }

    private addOAuthCredential(connection: IOAuthConnectionAuthInfo) {
      return this.AddOAuthCredentialAction.attemptAuth(connection.connectionType);
    }

    private encryptKeychains(keychains: UnencryptedConnectionKeychain[], publicKey: rsa.IRSAPublicKey): EncryptedConnectionKeychain[] {
      return keychains.map(
        keychain => {
          if (this.isOAuthKeychain(keychain)) {
            return keychain;
          } else {
            const encryptedPassword = rsa.encrypt(keychain.unencryptedPassword, publicKey);
            return {
              connection: keychain.connection,
              username: keychain.username,
              authenticationMode: keychain.authenticationMode,
              embedCredentials: keychain.embedCredentials,
              encryptedPassword: encryptedPassword
            };
          }
        }
      );
    }

    private getOAuthAdvisoryText(connection: IOAuthConnectionAuthInfo) {
      const stringId = 'embeddedVizAuthAccountAdvisory';
      const buttonText = this.$translate.instant('signIn');
      const typeDisplayName = connection.connectionTypeDisplayName;
      return this.$translate.instant(stringId, { BUTTON_HTML: buttonText, TYPE_NAME: typeDisplayName });
    }

    private getOAuthCredentials(connection: IOAuthConnectionAuthInfo, OAuthCredentials: OAuthCredentials) {
      const credentialsQuery = OAuthCredentials.all().addFilter(Filter.fieldEq('type', connection.connectionType));

      return SliceableHelper.fetchAll(credentialsQuery)
        .then(credentialsListResult => {
          return credentialsListResult.items;
        });
    }

    private isOAuthKeychain(keychain: UnencryptedConnectionKeychain): keychain is IOAuthKeychain {
      return (<IOAuthKeychain>keychain).oAuthKeyId !== undefined;
    }

    private isWorkbookRevision(revision: IWorkbookRevision | FlowRevision): revision is IWorkbookRevision {
      return (<IWorkbookRevision>revision).workbookId !== undefined;
    }

    private isFlowRevision(revision: IWorkbookRevision | FlowRevision): revision is FlowRevision {
      return (<FlowRevision>revision).flowId !== undefined;
    }
}

Module.instance().service('CheckConnectionCredentialsAction', CheckConnectionCredentialsAction);
